/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { Container, Loader } from "semantic-ui-react";
import { uniq, flatten, values } from "lodash";
import { ThunkDispatch } from "redux-thunk";
import { Action } from "redux";
import { isRequestActive } from "redux-request-loading";
import { debounce } from "lodash";
import * as moment from "moment";

import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";
import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { RadioButtonQuestion, RadioButton } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { AnsweredQuestion, AnsweredQuestionWithId } from "@teamprevent/common/model";
import { FilesQuestion } from "@teamprevent/common/questionnaires/components/questions/FilesQuestion";
import { submitInvalidQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { ImmunisationTaskType } from "@teamprevent/common/consultations/models";
import { ImmunisationDecision, ImmunisationDecisionResult, ImmunisationForCase, NextSteps } from "@teamprevent/common/immunisation/models";
import { updateQuestion } from "@teamprevent/common/questionnaires/actions";
import { SavingIndicator } from "@teamprevent/common/components/SavingIndicator";
import { QuestionnaireState, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { LOAD_IMMUNISATION_REVIEW, LOAD_SAVED_IMMUNISATION_REVIEW } from "@teamprevent/common/questionnaires/components/immunisationReview/actionTypes";
import { CaseType } from "@teamprevent/common/model";
import { EvidenceType } from "@teamprevent/common/immunisation/models";
import { toast } from "@teamprevent/common/toasts";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { TaskModel, TaskTypes } from "@teamprevent/common/task/taskActions/models";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { ConfidentialitySection } from "../../consultations/components/ConfidentialitySection";
import { DataProtectionSection } from "../../consultations/components/DataProtectionSection";
import { submitImmunisationReview, loadImmunisationReview, saveImmunisationReview, loadSavedImmunisationReview } from "../actions";
import { ActionableTasksState } from "../../actionableTasks";
import { loadOhCaseDetail } from "../../crud/ohCase/actions";
import { ApplicationState } from "../../applicationState";
import { EmployeeRecord } from "../../crud/employeeRecord";
import { LinkedOhCase, PartialOhCaseDetailModel } from "../../crud/ohCase/model";
import { DidNotAttendButton } from "../../clinicalNotes/components/DidNotAttendButton";
import { AddTaskFormModel, TaskType } from "../../task/model";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { loadImmunisationTypeTasks, ImmunisationTypeTask } from "../../crud/immunisationTypeTask";
import { immunisationTypeTasksSelector } from "../../crud/immunisationTypeTask/selectors";
import { FollowUpTaskModel, SaveImmunisationReviewModel } from "../model";
import { IMMUNISATION_REVIEW_TASK, BOOK_IMMUNISATION_REVIEW_TASK } from "../../crud/immunisationTypeTask/model";
import { loadImmunisationTypes } from "../../crud/immunisationType";
import { ImmunisationType } from "../../crud/immunisationType";
import { activeImmunisationTypesSelector } from "../../crud/immunisationType/selectors";
import { AddNextTaskFormModel } from "../../tasks/models";
import { loadTaskTypes } from "../../tasks/actions";
import { ImmunisationCreateModel } from "../../immunisations/model";
import { AdminUserDetailModel, getUserSortableName } from "../../crud/adminUser/model";

import { TasksApi } from "../../tasks/tasksApi";

import { loadEmployeeRecordDetail } from "../../crud/employeeRecord/actions";
import { validateConfidentialitySection } from "../../consultations/components/confidentialitySectionValidation";

import { ImmunisationTypesSection } from "./ImmunisationTypesSection";
import { ImmunisationTypeSection } from "./ImmunisationTypeSection";
import { GeneralQuestionsSection } from "./GeneralQuestionsSection";
import { CovidVaccinationSection } from "./CovidVaccinationSection";

export const QUESTIONNAIRE_KEY = "ImmunisationReview";

export interface ImmunisationReviewStateProps {
    ohCaseId: string;
    customerId: string;
    employeeRecordId: string;
    taskId: string;
    task: TaskModel;
    ohCase: PartialOhCaseDetailModel;
    employeeRecord: EmployeeRecord;
    readonly: boolean;
    isLoading: boolean;
    didNotAttend: boolean;
    immunisationTypeTasks: ImmunisationTypeTask[];
    taskTypes: TaskType[];
    immunisationTypes: ImmunisationType[];
    eTag?: string;
    questionnaire: QuestionnaireState;
    linkedPpqCase: LinkedOhCase;
    adminUsers?: AdminUserDetailModel[];
}

export interface ImmunisationReviewDispatchProps {
    loadOhCaseDetails: (ohCaseId: string, customerId: string) => void;
    submitImmunisationReview: (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: FollowUpTaskModel[],
        immunisationDecisions: { immunisationTypeId: string; immunisationDecision: ImmunisationDecision }[],
        immunisationRecords: ImmunisationCreateModel[]) => Promise<void>;
    navigateBack: () => void;
    loadImmunisationReview: (ohCaseId: string, taskId: string) => void;
    submitInvalidQuestionnaire: () => void;
    loadImmunisationTypeTasks: (immunisationTypeId: string) => void;
    loadImmunisationTypes: () => void;
    updateAnswer: (id: string, question: AnsweredQuestion, questionnaireKey: string, sectionId: string) => void;
    saveImmunisationReview: (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: { task: Partial<AddTaskFormModel>; isValid: boolean }[],
        immunisationTasks: { taskModel: AddNextTaskFormModel; immunisationTypeId: string }[],
        immunisationRecords: { immunisationModel: ImmunisationCreateModel; immunisationTypeId: string }[],
        eTag?: string) => Promise<void>;
    loadSavedImmunisationReview: (ohCaseId: string, taskId: string) => Promise<SaveImmunisationReviewModel>;
    loadTaskTypes: (customerId: string, ohCaseId: string) => void;
    loadEmployeeRecordDetail: (employeeRecordId: string, customerId: string) => void;
}

const ImmunisationReviewUnconnected: React.FunctionComponent<ImmunisationReviewStateProps & ImmunisationReviewDispatchProps> = (props) => {
    const eppHepBSurfaceAntigenQuestionId = "EppHepBSurfaceAntigen";
    const eppHepCAntibodiesQuestionId = "EppHepCAntibodies";
    const eppHivAntibodiesQuestionId = "EppHivAntibodies";
    const sufficientEvidenceId = "SufficientEvidence";

    const getAnswerToQuestionWithId = (id: string, sectionId?: string) => {
        if (questions) {
            const question = questions.find(q => q.id === id && (!sectionId || q.sectionId === sectionId));
            if (question) {
                return question.answer;
            }
        }

        return undefined;
    };

    const hasAnyEppEvidenceBeenProvided =
        getAnswerToQuestionWithId(eppHepBSurfaceAntigenQuestionId) === "Yes" ||
        getAnswerToQuestionWithId(eppHepCAntibodiesQuestionId) === "Yes" ||
        getAnswerToQuestionWithId(eppHivAntibodiesQuestionId) === "Yes";

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [tasks, setTasks] =
        React.useState<({ task: Partial<AddTaskFormModel>; isValid: false } | { task: AddTaskFormModel; isValid: true })[]>(
            [{ task: { taskType: null, dueDate: null, notes: undefined, taskCode: undefined }, isValid: false }]
        );
    const [savedTasks, setSavedTasks] =
        React.useState<{ task: AddTaskFormModel; isValid: boolean }[]>([]);
    const [savedTasksUpdated, setSavedTasksUpdated] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!savedTasksUpdated) {
            setSavedTasks([]);
        }
    }, [savedTasksUpdated]);

    const [currentImmunisationDecisions, setCurrentImmunisationDecisions] = React.useState<ImmunisationDecisionResult>({});
    const [initialImmunisationDecisions, setInitialImmunisationDecisions] = React.useState<ImmunisationDecisionResult>({});

    const onImmunisationTypeChange = (id: string, immunisationDecision: keyof typeof ImmunisationDecision) => {
        const decisions = { ...currentImmunisationDecisions };
        decisions[id] = ImmunisationDecision[immunisationDecision];
        setCurrentImmunisationDecisions(decisions);

        if (immunisationDecision === ImmunisationDecision.RequiredNoEvidence) {
            props.loadImmunisationTypeTasks(id);
        }
    };

    const [selectedTasks, setSelectedTasks] = React.useState<{ taskModel: AddNextTaskFormModel; immunisationTypeId: string }[]>([]);
    const [nextTasksUpdated, setNextTasksUpdated] = React.useState<boolean>(false);

    const onTaskChange = (taskTypeId: keyof typeof TaskTypes, immunisationTypeId: string) => {
        if (props.readonly) {
            return;
        }

        setNextTasksUpdated(false);

        if (!taskTypeId || !immunisationTypeId) {
            return;
        }

        const nextStepTask = NextSteps.find(t => t.taskTypeId === taskTypeId.toString());

        if (!nextStepTask) {
            return;
        }

        let selectedTask = selectedTasks.find(t => t.immunisationTypeId === immunisationTypeId);

        if (selectedTask?.taskModel?.task?.taskType?.typeId === taskTypeId) {
            return;
        }

        setNextTasksUpdated(true);

        if (!selectedTask) {
            selectedTask = {
                taskModel: {
                    task: null,
                    isValid: false
                },
                immunisationTypeId
            };
        }

        selectedTask.taskModel.task = {
            taskType: {
                type: taskTypeId,
                typeId: taskTypeId,
                description: nextStepTask.taskName,
                subTasks: [],
                dueDateApplicable: true,
                maximumNumberOfDaysDueDateCanBeInFuture: 7 * 8,
                closesCase: false,
                isBookTask: nextStepTask.isBookTask,
                sortNumber: selectedTask!.taskModel?.task?.taskType?.sortNumber ?? selectedTasks.length,
                immunisationTaskType: nextStepTask.immunisationTaskType,
                notesRequired: false,
                visibleForCaseTypes: [props.ohCase.caseType]
            },
            taskCode: nextStepTask.taskCode,
            notes: "",
            dueDate: null
        };

        setSelectedTasks([...selectedTasks.filter(t => t.immunisationTypeId !== immunisationTypeId), selectedTask!]);
    };

    const [saving, setSaving] = React.useState(false);
    const [loadComplete, setLoadComplete] = React.useState(false);
    const [eTag, setETag] = React.useState<string>();
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            if (!(props.ohCase && props.ohCase.immunisations)) {
                props.loadOhCaseDetails(props.ohCaseId, props.customerId);
            }

            if (props.readonly && !props.didNotAttend) {
                props.loadImmunisationReview(props.ohCaseId, props.taskId);
            }

            if (props.customerId && props.ohCaseId && !(props.taskTypes.length > 0)) {
                props.loadTaskTypes(props.customerId, props.ohCaseId);
            }

            await props.loadImmunisationTypes();

            if (props?.readonly === false) {
                const savedImmunisationReview = await props.loadSavedImmunisationReview(props.ohCaseId, props.taskId);
                if (savedImmunisationReview) {
                    setTasks(savedImmunisationReview.tasksData?.tasks);
                    setSavedTasks(savedImmunisationReview.tasksData?.tasks);
                    setSavedTasksUpdated(true);

                    if (savedImmunisationReview.immunisationTasksData?.tasks) {
                        setSelectedTasks(savedImmunisationReview.immunisationTasksData.tasks);
                    }

                    if (savedImmunisationReview.immunisationRecordsData?.immunisations) {
                        setImmunisationRecords(
                            savedImmunisationReview.immunisationRecordsData.immunisations.map(
                                (i, index) => {
                                    return {
                                        immunisationTypeId: i.immunisationTypeId,
                                        immunisationModel: {
                                            ...i.immunisationModel,
                                            dateCompleted: i.immunisationModel?.dateCompleted && moment(i.immunisationModel?.dateCompleted)
                                        },
                                        rowId: index
                                    };
                                }));
                    }

                    setETag(savedImmunisationReview.eTag);

                    const immunisationTypeQuestions = getImmunisationTypeTasks(savedImmunisationReview.questionsData?.questions);

                    const decisions: ImmunisationDecisionResult = {};

                    immunisationTypeQuestions?.forEach(i => {
                        decisions[i.id!] = ImmunisationDecision[i.answer?.toString() as keyof typeof ImmunisationDecision];
                        if (decisions[i.id] === ImmunisationDecision.RequiredNoEvidence) {
                            props.loadImmunisationTypeTasks(i.id);
                        }
                    });

                    setCurrentImmunisationDecisions(decisions);
                }
                else {
                    const decisions = { ...initialImmunisationDecisions };

                    const caseImmunisations = (props.ohCase?.immunisations ?? []).concat(props.ohCase?.additionalImmunisations ?? []);

                    caseImmunisations.forEach(i => {
                        const typeId = `ImmunisationType_${i.typeId}`;

                        decisions[typeId] = ImmunisationDecision[i.immunisationDecision];
                        if (decisions[typeId] === ImmunisationDecision.RequiredNoEvidence) {
                            props.loadImmunisationTypeTasks(i.typeId);
                        }
                    });

                    setCurrentImmunisationDecisions(decisions);
                    setInitialImmunisationDecisions(decisions);
                }
                setLoadComplete(true);
            }

            props.loadEmployeeRecordDetail(props.employeeRecordId, props.customerId);
        })();
    }, [props.readonly]);

    const getImmunisationTypeName = (id: string) => {
        return (props.immunisationTypes ?? []).find(t => t.id === id.replace("ImmunisationType_", ""))?.name;
    };

    React.useEffect(() => {
        if (loadComplete) {
            (Object.keys(currentImmunisationDecisions) as (keyof typeof currentImmunisationDecisions)[])
                .map(i => {
                    props.updateAnswer(
                        i,
                        {
                            question: getImmunisationTypeName(i),
                            sectionId: "ImmunisationTypes",
                            id: i,
                            answer: currentImmunisationDecisions[i],
                            isValid: true
                        },
                        QUESTIONNAIRE_KEY,
                        "ImmunisationTypes");
                });
        }
    }, [currentImmunisationDecisions, loadComplete]);

    const [immunisationRecords, setImmunisationRecords] =
        React.useState<{ immunisationModel: ImmunisationCreateModel; immunisationTypeId: string; rowId: number }[]>([]);

    const onImmunisationsAdd = (immunisation: ImmunisationCreateModel, immunisationTypeId: string) => {
        const immunisationRecordsMap = immunisationRecords.map((i, index) => {
            return {
                ...i,
                rowId: index
            };
        });

        const newImmunisation = { immunisationModel: immunisation, immunisationTypeId, rowId: immunisationRecords.length };

        setImmunisationRecords([...immunisationRecordsMap, newImmunisation]);
    };

    const onImmunisationsRemoveForImmunisationTypeId = (immunisationTypeId: string) => {
        setImmunisationRecords([...immunisationRecords.filter(i => i.immunisationTypeId !== immunisationTypeId)]);
    };

    const onImmunisationsRemoveForRowId = (rowId: number) => {
        setImmunisationRecords([...immunisationRecords.filter(i => i.rowId !== rowId)]);
    };

    const getImmunisationTypeTasks = (savedQuestions: AnsweredQuestionWithId[]) =>
        savedQuestions?.filter(q => q.id.startsWith("ImmunisationType"));

    const save = async (
        questionsToSave: AnsweredQuestionWithId[],
        tasksToSave: { task: Partial<AddTaskFormModel>; isValid: boolean }[],
        immunisationTasksToSave: { taskModel: AddNextTaskFormModel; immunisationTypeId: string }[],
        immunisationRecordsToSave: { immunisationModel: ImmunisationCreateModel; immunisationTypeId: string }[]) => {
        if (props.readonly) {
            return;
        }

        setSaving(true);
        try {
            await props.saveImmunisationReview(
                props.ohCaseId,
                props.taskId,
                questionsToSave,
                tasksToSave,
                immunisationTasksToSave,
                immunisationRecordsToSave,
                props.eTag ?? eTag);
        } finally {
            setSaving(false);
        }
    };

    const saveDebounced = debounce(async (
        questionsToSave: AnsweredQuestionWithId[],
        tasksToSave: { task: Partial<AddTaskFormModel>; isValid: boolean }[],
        immunisationTasksToSave: { taskModel: AddNextTaskFormModel; immunisationTypeId: string }[],
        immunisationRecordsToSave: { immunisationModel: ImmunisationCreateModel; immunisationTypeId: string }[]) => {
        if (!props.saveImmunisationReview || saving || submitting) {
            return;
        }

        await save(questionsToSave, tasksToSave, immunisationTasksToSave, immunisationRecordsToSave);
    }, 1500);

    const immunisationTypeQuestions = React.useMemo(() =>
        props.readonly &&
            props.questionnaire &&
            props.questionnaire.sections?.find(s => s.id === "ImmunisationTypes") ?
            values(props.questionnaire.sections?.
                find(s => s.id === "ImmunisationTypes")?.questions)?.
                filter(q => q.id.startsWith("ImmunisationType") && q.answer === "RequiredNoEvidence") :
            [],
    [props.questionnaire, props.readonly]);

    const distinctSelectedTasks = React.useMemo(() => {
        const filteredTasks = selectedTasks
            .map((value, index, self) =>
                self.findIndex(s =>
                    value.taskModel?.task?.taskType?.typeId ===
                    s.taskModel?.task?.taskType?.typeId) === index ?
                    value : null)
            .filter((s): s is Exclude<typeof s, null> => !!s);

        return filteredTasks
            .sort(s => s.taskModel?.task?.taskType?.sortNumber).map(s => s.taskModel);
    },
    [selectedTasks]);

    const questions = React.useMemo(() =>
        props.questionnaire ?
            uniq(flatten(props.questionnaire.sections.map(section => values(section.questions)))) :
            [],
    [props.questionnaire]);

    React.useEffect(() => {
        if (props.readonly || !questions || !tasks || !immunisationRecords || !loadComplete || props.isLoading) {
            return undefined;
        }
        saveDebounced(questions, tasks, selectedTasks, immunisationRecords);
        return saveDebounced.cancel;
    }, [props.questionnaire?.sections, tasks, loadComplete, immunisationRecords, props.isLoading]);

    const renderImmunisationEvidenceSection = (immunisation: ImmunisationForCase) => {
        return (
            <AccordionQuestionnaireSection
                index={5 + (props?.immunisationTypes?.length ?? 0)}
                key={immunisation.typeId}
                id={`EvidenceOfImmunity_${immunisation.typeId}`}
                title={`Immunity to ${immunisation.name}`}
            >
                <MultiButtonQuestion
                    readonly={props.readonly}
                    mandatory
                    questionConfig={({
                        id: "SufficientEvidence",
                        questionText: `Has Sufficient Previous documentary evidence of immunity been provided for ${immunisation.name}?`
                    })}
                />
                {getAnswerToQuestionWithId(sufficientEvidenceId, `EvidenceOfImmunity_${immunisation.typeId}`) === MultiButtonQuestion.YesOption &&
                    <>
                        <OldDateQuestion
                            readonly={props.readonly}
                            questionConfig={({ id: "DateCompleted", questionText: "Date completed" })}
                            mandatory
                        />
                        <FilesQuestion
                            readonly={props.readonly}
                            questionConfig={({ id: "Evidence", questionText: "Evidence" })}
                            api={`/api/immunisationReview/${props.ohCaseId}/evidence`}
                            mandatory
                            acceptExtensions={acceptableExtensions}
                        />
                    </>}
            </AccordionQuestionnaireSection>);
    };

    const nextTasksRequired = () =>
        !props.readonly &&
        ((questions.some(q => q.id.startsWith("NewEvidence") && q.answer === MultiButtonQuestion.NoOption)) ||
            (questions.some(q => q.id.startsWith("CanUploadEvidence") && q.answer === MultiButtonQuestion.NoOption)));

    const submitQuestionnaire = async (questionsToSubmit: AnsweredQuestionWithId[]): Promise<boolean> => {
        if (props.readonly) {
            return false;
        }

        setSubmitted(true);

        const invalidImmunisationTypes: string[] = [];

        questions.filter(q =>
            q.id.startsWith("TypeOfEvidence") &&
            (q.answer === EvidenceType.bcgScar ||
                q.answer === EvidenceType.historyOfDisease ||
                q.answer === EvidenceType.historyOfvaccination ||
                q.answer === EvidenceType.historyOfPreviousVaricellaInfectionInTheUK))
            .forEach(q => {

                const immunisationTypeId = q.id.replace("TypeOfEvidence_ImmunisationType_", "").replace("TypeOfEvidence_", "");

                if (!immunisationRecords.some(ir => ir.immunisationTypeId?.replace("ImmunisationType_", "") === immunisationTypeId)) {
                    const sectionName =
                        immunisationTypeId === "CovidVaccinationSection" ? "COVID-19 Vaccination" : getImmunisationTypeName(immunisationTypeId);
                    invalidImmunisationTypes.push(sectionName);
                }
            });

        if (invalidImmunisationTypes.length > 0) {
            invalidImmunisationTypes.forEach(t => {
                toast.warning(`At-least 1 immunisation record should be added for immunisation type - ${t}`);
            });

            return false;
        }

        const immunisationWorkflowImprovementsPt1 = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

        const validationResult = validateConfidentialitySection(questions);

        if (immunisationWorkflowImprovementsPt1 && !validationResult.isValid) {
            toast.warning(validationResult.error);
            return false;
        }

        saveDebounced.cancel();
        save(questionsToSubmit, tasks, selectedTasks, immunisationRecords);

        setSubmitting(true);

        try {
            const submitTasks = nextTasksRequired();
            if (!questionsToSubmit.every(question => question.isValid) || (submitTasks && !tasks.every(t => t.isValid))) {
                props.submitInvalidQuestionnaire();
                return false;
            } else {
                const taskModels: FollowUpTaskModel[] = submitTasks ?
                    tasks.map(t => ({
                        taskType: t.task.taskType.type,
                        immunisationTypeTasks:
                            t.task.immunisationTypeTaskIds
                                ? t.task.immunisationTypeTaskIds.filter(it =>
                                    props.immunisationTypeTasks
                                        .filter(i =>
                                            (Object.keys(currentImmunisationDecisions) as (keyof typeof currentImmunisationDecisions)[])
                                                .filter(c => currentImmunisationDecisions[c] === ImmunisationDecision.RequiredNoEvidence)
                                                .some(c => c.replace("ImmunisationType_", "") === i.immunisationTypeId))
                                        .some(tt => tt.id === it))
                                : null,
                        notes: t.task.notes,
                        taskCodeId: t.task.taskCode,
                        dueDate: t.task.dueDate,
                        assignedUsers: t.task.assignedUsers
                    })) : [];

                const immunisationDecisionsList = (Object.keys(currentImmunisationDecisions) as (keyof typeof currentImmunisationDecisions)[])
                    .map(k => {
                        return {
                            immunisationTypeId: k,
                            immunisationDecision: currentImmunisationDecisions[k]
                        };
                    });
                await props.submitImmunisationReview(
                    props.ohCaseId,
                    props.taskId,
                    questionsToSubmit,
                    taskModels,
                    immunisationDecisionsList,
                    immunisationRecords.map(i => i.immunisationModel));
                props.navigateBack();

                return true;
            }
        } finally {
            setSubmitting(false);
        }
    };

    const loadQuestionnaire = React.useCallback((): null => null, []);

    if (props.isLoading) {
        return <Loader active />;
    }

    const isAnyImmunisationTypeQuestionAnswered = () => {
        return (questions ?? []).some(question => question.id.startsWith("ImmunisationType"));
    };

    const doesAnyQuestionExistWithMatchingSectionId = (sectionId: string) => {
        return (questions ?? []).some(question => question.sectionId === sectionId);
    };

    const doesAnyQuestionExistWhereSectionIdStartsWith = (sectionId: string) => {
        return (questions ?? []).some(question => question.sectionId?.startsWith(sectionId));
    };

    const updateSnippets = (snippetText: string, sectionId: string, id: string, questionText: string) => {
        const questionAnswer = getAnswerToQuestionWithId(id);

        const question = {
            question: questionText,
            answer: (questionAnswer ? questionAnswer + " " : "") + snippetText,
            isValid: true
        };

        props.updateAnswer(id, question, QUESTIONNAIRE_KEY, sectionId);
    };

    return (
        <Container>
            <h2>Immunisation Review</h2>
            {!props.readonly && <Container textAlign="right" className="dna-button-container">
                <DidNotAttendButton
                    customerId={props.customerId}
                    ohCaseId={props.ohCaseId}
                    task={props.task}
                />
            </Container>}
            {!props.isLoading && (loadComplete || props.readonly) &&
                <AccordionQuestionnaireContainer
                    loadQuestionnaire={loadQuestionnaire}
                    submitQuestionnaire={submitQuestionnaire}
                    questionnaireKey={QUESTIONNAIRE_KEY}
                    readonly={props.readonly}
                    questionnaireType={QuestionnaireTypes.ClinicalNotes}
                    task={props.task}
                    removeAppointment={() => new TasksApi().removeAppointment(props.task.caseId, props.task.id)}
                >
                    <>
                        {(!props.readonly || isAnyImmunisationTypeQuestionAnswered()) &&
                        <ImmunisationTypesSection
                            index={1}
                            readonly={props.readonly}
                            questionnaireKey={QUESTIONNAIRE_KEY}
                            immunisationTypes={props.immunisationTypes}
                            onImmunisationTypeChange={onImmunisationTypeChange}
                            initialImmunisationDecisions={props.readonly ? {} : initialImmunisationDecisions}
                        />
                        }

                        <ConfidentialitySection
                            index={2}
                            ohCase={props.ohCase}
                            readonly={props.readonly}
                            employeeRecord={props.employeeRecord}
                            questionnaireKey={QUESTIONNAIRE_KEY}
                        />
                        <DataProtectionSection
                            index={3}
                            readonly={props.readonly}
                            questionnaireKey={QUESTIONNAIRE_KEY}
                        />
                        {props.readonly === false &&
                        <>
                            {props.immunisationTypes
                                .filter(i =>
                                    (Object.keys(currentImmunisationDecisions) as (keyof typeof currentImmunisationDecisions)[])
                                        .filter(c => currentImmunisationDecisions[c] === ImmunisationDecision.RequiredNoEvidence)
                                        .findIndex(c => c === `ImmunisationType_${i.id}`) >= 0)
                                .map(i =>
                                    (<ImmunisationTypeSection
                                        index={4 + props.immunisationTypes.findIndex(t => t.id === i.id)}
                                        key={`ImmunisationType_${i.id}`}
                                        ohCaseId={props.ohCaseId}
                                        readonly={props.readonly}
                                        questionnaireKey={QUESTIONNAIRE_KEY}
                                        immunisationTypeId={`ImmunisationType_${i.id}`}
                                        immunisationTypeName={getImmunisationTypeName(`ImmunisationType_${i.id}`)}
                                        questions={questions}
                                        onTaskChange={onTaskChange}
                                        updateAnswer={props.updateAnswer}
                                        immunisationRecords={immunisationRecords}
                                        employeeRecordId={props.employeeRecord.id}
                                        onImmunisationsAdd={onImmunisationsAdd}
                                        onImmunisationsRemoveForRowId={onImmunisationsRemoveForRowId}
                                        onImmunisationsRemoveForImmunisationTypeId={onImmunisationsRemoveForImmunisationTypeId}
                                    />
                                    ))
                            }
                        </>
                        }
                        {props.readonly &&
                        <>
                            {immunisationTypeQuestions
                                .map(q =>
                                    (<ImmunisationTypeSection
                                        index={4 + immunisationTypeQuestions.indexOf(q)}
                                        key={q.id}
                                        ohCaseId={props.ohCaseId}
                                        readonly={props.readonly}
                                        questionnaireKey={QUESTIONNAIRE_KEY}
                                        immunisationTypeId={q.id}
                                        immunisationTypeName={q.question}
                                        questions={questions}
                                        onTaskChange={onTaskChange}
                                        updateAnswer={props.updateAnswer}
                                        immunisationRecords={immunisationRecords}
                                        employeeRecordId={props.employeeRecord.id}
                                        onImmunisationsAdd={onImmunisationsAdd}
                                        onImmunisationsRemoveForRowId={onImmunisationsRemoveForRowId}
                                        onImmunisationsRemoveForImmunisationTypeId={onImmunisationsRemoveForImmunisationTypeId}
                                    />
                                    ))
                            }
                        </>
                        }
                        {props.linkedPpqCase?.evidenceOfCovid19VaccinationStatusMandatory &&
                        <CovidVaccinationSection
                            index={4 + (props?.immunisationTypes?.length ?? 0)}
                            ohCaseId={props.ohCaseId}
                            readonly={props.readonly}
                            questionnaireKey={QUESTIONNAIRE_KEY}
                            questions={questions}
                            onTaskChange={onTaskChange}
                            updateAnswer={props.updateAnswer}
                            immunisationRecords={immunisationRecords}
                            employeeRecordId={props.employeeRecord.id}
                            onImmunisationsAdd={onImmunisationsAdd}
                            onImmunisationsRemoveForRowId={onImmunisationsRemoveForRowId}
                            onImmunisationsRemoveForImmunisationTypeId={onImmunisationsRemoveForImmunisationTypeId}
                        />
                        }
                        {props.readonly &&
                        doesAnyQuestionExistWhereSectionIdStartsWith("EvidenceOfImmunity") &&
                        props.ohCase &&
                        props.ohCase.immunisations &&
                        props.ohCase.immunisations.map(renderImmunisationEvidenceSection)}

                        {props.readonly && doesAnyQuestionExistWithMatchingSectionId("EvidenceEppWorker") &&
                        <AccordionQuestionnaireSection
                            id="EvidenceEppWorker"
                            title="Blood tests for an EPP Worker"
                            index={6 + (props?.immunisationTypes?.length ?? 0)}>
                            <p>Has Sufficient Previous documentary evidence of completed Blood tests for an EPP Worker been provided?</p>
                            <RadioButtonQuestion
                                questionConfig={({ id: eppHepBSurfaceAntigenQuestionId, questionText: "EPP Hepatitis B Surface Antigen" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={({ id: eppHepCAntibodiesQuestionId, questionText: "EPP Hepatitis C Antibodies" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={({ id: eppHivAntibodiesQuestionId, questionText: "EPP HIV Antibodies" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            {hasAnyEppEvidenceBeenProvided &&
                                <MultiButtonQuestion
                                    questionConfig={({
                                        id: "EvidenceMainEmployeeRecord",
                                        questionText: "Has the above evidence provided been updated in the main employee Immunisation Record?"
                                    })}
                                    readonly={props.readonly}
                                    validationDependencies={[eppHepBSurfaceAntigenQuestionId, eppHepCAntibodiesQuestionId, eppHivAntibodiesQuestionId]}
                                    mandatory
                                />}
                        </AccordionQuestionnaireSection>
                        }
                        {(!props.readonly || doesAnyQuestionExistWithMatchingSectionId("GeneralQuestions")) &&
                        <GeneralQuestionsSection
                            index={7 + (props?.immunisationTypes?.length ?? 0)}
                            readonly={props.readonly}
                            questionnaireKey={QUESTIONNAIRE_KEY}
                        />
                        }
                        <AccordionQuestionnaireSection
                            id="AdditionalComments"
                            title="Additional Comments"
                            index={8 + (props?.immunisationTypes?.length ?? 0)}
                        >
                            <TextQuestion
                                questionConfig={({
                                    id: "AdditionalComments",
                                    questionText: "Additional Comments Notes"
                                })}
                                readonly={props.readonly}
                                multiline
                                textSnippetSelector={
                                    <TextSnippetSelector
                                        onSelect={selectedSnippet => updateSnippets(
                                            selectedSnippet,
                                            "AdditionalComments",
                                            "AdditionalComments",
                                            "Additional Comments Notes")
                                        }
                                        padded
                                    />
                                }
                            />
                        </AccordionQuestionnaireSection>
                        {props.readonly && doesAnyQuestionExistWithMatchingSectionId("VaccinesGiven") &&
                        <AccordionQuestionnaireSection id="VaccinesGiven" title="Vaccines Given" index={9 + (props?.immunisationTypes?.length ?? 0)}>
                            <p>Please confirm if any of the following vaccines have been given, where appropriate e.g Lab Workers and Estates Workers?</p>
                            <RadioButtonQuestion
                                questionConfig={({ id: "DipTetPolio", questionText: "Dip/Tet/Polio" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={({ id: "Mumps", questionText: "Mumps" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={({ id: "Typhoid", questionText: "Typhoid" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={({ id: "HepatitisA", questionText: "Hepatitis A" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={({
                                    id: "NonResponderHepB",
                                    questionText: "If known non-responder to Hepatitis B has an advice leaflet been given?"
                                })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={({ id: "BbvProcessDiscussed", questionText: "Has the Needle stick/BBV incident reporting process discussed?" })}
                                readonly={props.readonly}
                                mandatory
                            >
                                <RadioButton value="Yes" label="Yes" />
                                <RadioButton value="No" label="No" />
                                <RadioButton value="N/A" label="N/A" />
                            </RadioButtonQuestion>
                        </AccordionQuestionnaireSection>
                        }
                        {nextTasksRequired() &&
                        <AccordionQuestionnaireSection
                            index={10 + (props?.immunisationTypes?.length ?? 0)}
                            id="NextTasks"
                            title="Next Tasks"
                            neverHide={true}
                            additionalIsValid={tasks.length > 0 && tasks.every(t => t.isValid)}
                        >
                            <AddTasksInputs
                                immunisationTypeTasks={props.immunisationTypeTasks
                                    .filter(i =>
                                        (Object.keys(currentImmunisationDecisions) as (keyof typeof currentImmunisationDecisions)[])
                                            .filter(c => currentImmunisationDecisions[c] === ImmunisationDecision.RequiredNoEvidence)
                                            .some(c => c.replace("ImmunisationType_", "") === i.immunisationTypeId))}
                                onChange={setTasks}
                                showErrors={submitted}
                                taskTypes={props.taskTypes}
                                ohCaseId={props.ohCaseId}
                                caseType={props.ohCase?.caseType}
                                savedTasks={savedTasks}
                                forceSavedTasksUpdate={savedTasksUpdated}
                                onSavedTasksUpdate={setSavedTasksUpdated}
                                updateTasks={distinctSelectedTasks}
                                forceTasksUpdate={nextTasksUpdated}
                                onTasksUpdate={setNextTasksUpdated}
                                assignmentOptionRequired={true}
                            />
                        </AccordionQuestionnaireSection>}
                    </>
                </AccordionQuestionnaireContainer>
            }
            <SavingIndicator display={saving} />
        </Container>);
};

const mapStateToProps = (state: ApplicationState & ActionableTasksState): ImmunisationReviewStateProps => {
    let ohCaseId = state.router.params!.ohCaseId;
    const taskId = state.router.params!.taskId;
    let task;
    if (!ohCaseId) {
        task = state.actionableTasks.tasks.find(t => t.id === taskId);
        ohCaseId = task.caseId;
    }

    const ohCase = state.ohCases.find(c => c.id === ohCaseId);
    if (!task && ohCase) {
        task = ohCase.tasks.find(t => t.id === taskId);
    }

    let linkedPpqCase;
    if (ohCase) {
        linkedPpqCase = ohCase?.linkedOhCases?.find(l => l.caseType === CaseType.PPQ);
    }

    const customerId = state.router.params!.customerId || task.customer.id;
    const employeeRecordId = state.router.params!.employeeRecordId || (ohCase && ohCase.employeeRecordId);
    const employeeRecord = state.employeeRecordsDetail.employeeRecords.find(e => e.id === employeeRecordId) as EmployeeRecord;

    const adminUsers: AdminUserDetailModel[] = [...state.adminUsers.sort((a, b) => getUserSortableName(a).localeCompare(getUserSortableName(b)))];

    adminUsers.splice(0, 0, { id: "Unassigned", emailAddress: "Unassigned" });

    return {
        readonly: task?.completed || task?.cancelled,
        taskId,
        task,
        customerId,
        employeeRecordId,
        ohCaseId,
        ohCase,
        employeeRecord,
        questionnaire: state.questionnaires[QUESTIONNAIRE_KEY],
        isLoading: !task ||
            !ohCase ||
            !employeeRecord ||
            (task.completed && isRequestActive(state, LOAD_IMMUNISATION_REVIEW)) ||
            isRequestActive(state, LOAD_SAVED_IMMUNISATION_REVIEW) ||
            isRequestActive(state, "loadImmunisationTypes"),
        didNotAttend: task?.didNotAttend,
        immunisationTypeTasks: immunisationTypeTasksSelector(state),
        taskTypes: state.tasks.taskTypes.filter(
            t => [IMMUNISATION_REVIEW_TASK, BOOK_IMMUNISATION_REVIEW_TASK, ImmunisationTaskType.Evidence].indexOf(t.type) === -1
        ),
        immunisationTypes: activeImmunisationTypesSelector(state),
        eTag: state.questionnaires[QUESTIONNAIRE_KEY]?.eTag,
        linkedPpqCase,
        adminUsers
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): ImmunisationReviewDispatchProps => ({
    loadOhCaseDetails: (ohCaseId: string, customerId: string) => dispatch(loadOhCaseDetail({ ohCaseId, customerId })),
    submitImmunisationReview: (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: FollowUpTaskModel[],
        immunisationDecisions: { immunisationTypeId: string; immunisationDecision: ImmunisationDecision }[],
        immunisationRecords: ImmunisationCreateModel[]) =>
        dispatch(submitImmunisationReview(ohCaseId, taskId, questions, QUESTIONNAIRE_KEY, tasks, immunisationDecisions, immunisationRecords)),
    navigateBack: () => dispatch(navigateToParentRoute()),
    loadImmunisationReview: (ohCaseId: string, taskId: string) => dispatch(loadImmunisationReview(ohCaseId, taskId, QUESTIONNAIRE_KEY)),
    submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(QUESTIONNAIRE_KEY)),
    loadImmunisationTypeTasks: (immunisationTypeId: string) =>
        dispatch(loadImmunisationTypeTasks({ immunisationTypeId: immunisationTypeId?.replace("ImmunisationType_", "") })),
    loadImmunisationTypes: () => dispatch(loadImmunisationTypes()),
    updateAnswer: (id, question, questionnaireKey, sectionId) => dispatch(updateQuestion({ id, question, questionnaireKey, sectionId })),
    saveImmunisationReview: (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        tasks: { task: AddTaskFormModel; isValid: boolean }[],
        immunisationTasks: { taskModel: AddNextTaskFormModel; immunisationTypeId: string }[],
        immunisationRecords: { immunisationModel: ImmunisationCreateModel; immunisationTypeId: string }[],
        eTag?: string) =>
        dispatch(saveImmunisationReview(ohCaseId, taskId, questions, QUESTIONNAIRE_KEY, tasks, immunisationTasks, immunisationRecords, eTag)),
    loadSavedImmunisationReview: (ohCaseId: string, taskId: string) => dispatch(loadSavedImmunisationReview(ohCaseId, taskId, QUESTIONNAIRE_KEY)),
    loadTaskTypes: (customerId: string, ohCaseId: string) => dispatch(loadTaskTypes(customerId, ohCaseId)),
    loadEmployeeRecordDetail: (employeeRecordId: string, customerId: string) => dispatch(loadEmployeeRecordDetail({ employeeRecordId, customerId }))
});

export const ImmunisationReview = connect(mapStateToProps, mapDispatchToProps)(ImmunisationReviewUnconnected);
